/**
 * /civic — Social Pulse Civic Reporting Hub.
 *
 * Tabs: Feed · Map · Submit · Stats · Leaderboard
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SmartTextInput from "@/components/SmartTextInput";
import "leaflet/dist/leaflet.css";
import {
  AlertTriangle, Camera, ChevronRight, Filter, MapPin, MessageCircle,
  Send, Sparkles, TrendingUp, Trophy, ThumbsUp, CheckCircle2, XCircle,
  Loader2, Upload, ArrowLeft,
} from "lucide-react";

const SEVERITIES = [
  { v: "emergency", label: "Emergency", dot: "#DC2626" },
  { v: "urgent",    label: "Urgent",    dot: "#EA580C" },
  { v: "high",      label: "High",      dot: "#CA8A04" },
  { v: "normal",    label: "Normal",    dot: "#16A34A" },
  { v: "suggestion",label: "Suggestion",dot: "#2563EB" },
];
const STATUSES = [
  { v: "open", label: "Open" },
  { v: "verified", label: "Verified" },
  { v: "in_progress", label: "In progress" },
  { v: "resolved", label: "Resolved" },
  { v: "closed", label: "Closed" },
];

const TABS = [
  { v: "feed", label: "Reports" },
  { v: "map", label: "Map" },
  { v: "submit", label: "Submit" },
  { v: "stats", label: "Dashboard" },
  { v: "leaders", label: "Top reporters" },
];

export default function Civic() {
  const [params, setParams] = useSearchParams();
  const initial = TABS.find((t) => t.v === params.get("tab"))?.v || "feed";
  const [tab, setTab] = useState(initial);
  const [coords, setCoords] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    http.get("/civic/categories").then((r) => setCategories(r.data.categories || [])).catch(() => setCategories([]));
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setCoords({ lat: 40.7128, lng: -74.006 }),
        { timeout: 6000 }
      );
    } else { setCoords({ lat: 40.7128, lng: -74.006 }); }
  }, []);

  const setActive = (v) => { setTab(v); setParams({ tab: v }); };
  const effective = coords || { lat: 40.7128, lng: -74.006 };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="civic-page">
      <div className="overline">SOCIALPULSE · CIVIC REPORTS</div>
      <div className="flex items-end justify-between flex-wrap gap-3 mt-2">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl">See it. Report it. Fix it, together.</h1>
          <p className="mt-3 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Potholes, broken lights, illegal dumping, unsafe buildings — your neighborhood on the record.
            Residents, volunteers, NGOs and government working side-by-side.
          </p>
        </div>
      </div>

      <div className="mt-8 border-b flex gap-1 overflow-x-auto" style={{ borderColor: "var(--wofy-border)" }}>
        {TABS.map(({ v, label }) => (
          <button
            key={v}
            data-testid={`civic-tab-${v}`}
            onClick={() => setActive(v)}
            className="px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap"
            style={{ borderColor: tab === v ? "var(--wofy-primary)" : "transparent", color: tab === v ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "feed" && <FeedTab categories={categories} coords={effective} />}
        {tab === "map" && <MapTab coords={effective} />}
        {tab === "submit" && <SubmitTab categories={categories} coords={effective} onSubmitted={() => setActive("feed")} />}
        {tab === "stats" && <StatsTab />}
        {tab === "leaders" && <LeadersTab />}
      </div>
    </div>
  );
}


function SeverityDot({ v }) {
  const s = SEVERITIES.find((x) => x.v === v) || SEVERITIES[3];
  return <span title={s.label} className="inline-block w-2 h-2 rounded-full" style={{ background: s.dot }} />;
}


/* -------------------- Feed -------------------- */
function FeedTab({ categories, coords }) {
  const [items, setItems] = useState(null);
  const [scope, setScope] = useState("all");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);  // report id for detail

  const load = useCallback(() => {
    const p = new URLSearchParams({ scope });
    if (category) p.set("category", category);
    if (status) p.set("status", status);
    if (severity) p.set("severity", severity);
    if (q) p.set("q", q);
    if (scope === "near") { p.set("lat", coords.lat); p.set("lng", coords.lng); }
    http.get(`/civic/reports?${p}`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [scope, category, status, severity, q, coords.lat, coords.lng]);

  useEffect(() => { load(); }, [load]);

  if (active) {
    return <ReportDetail id={active} onBack={() => { setActive(null); load(); }} />;
  }

  return (
    <div data-testid="civic-feed">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        {[["all", "All"], ["mine", "Mine"], ["near", "Nearby"]].map(([v, l]) => (
          <button key={v} data-testid={`civic-scope-${v}`} onClick={() => setScope(v)}
            className={`text-xs px-3 py-1.5 rounded-full border ${scope === v ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
            style={scope !== v ? { borderColor: "var(--wofy-border)" } : undefined}>{l}</button>
        ))}
        <select data-testid="civic-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--wofy-border)" }}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.key} value={c.key}>{c.icon} {c.name}</option>)}
        </select>
        <select data-testid="civic-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--wofy-border)" }}>
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <select data-testid="civic-severity-filter" value={severity} onChange={(e) => setSeverity(e.target.value)}
          className="text-xs rounded-full border px-3 py-1.5" style={{ borderColor: "var(--wofy-border)" }}>
          <option value="">Any severity</option>
          {SEVERITIES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <input data-testid="civic-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, address…" className="text-xs rounded-full border px-3 py-1.5 flex-1 max-w-xs" style={{ borderColor: "var(--wofy-border)" }} />
      </div>

      {items === null ? (
        <p className="text-sm">Loading reports…</p>
      ) : items.length === 0 ? (
        <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          No reports match those filters yet.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((r) => (
            <li key={r.id}>
              <button onClick={() => setActive(r.id)} data-testid={`civic-report-card-${r.id}`}
                className="w-full text-left rounded-xl border overflow-hidden bg-white flex flex-col hover:shadow-md transition"
                style={{ borderColor: "var(--wofy-border)" }}>
                {r.image_url ? (
                  <div className="aspect-video bg-[var(--wofy-surface)] overflow-hidden">
                    <img src={r.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className="aspect-video flex items-center justify-center text-4xl" style={{ background: r.category_color + "20" }}>
                    {r.category_icon}
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-wider">
                    <span className="px-2 py-0.5 rounded-full" style={{ background: r.category_color + "20", color: r.category_color }}>
                      {r.category_icon} {r.category_name}
                    </span>
                    <SeverityDot v={r.severity} /> <span style={{ color: "var(--wofy-text-soft)" }}>{r.severity}</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full" style={{ background: statusBg(r.status), color: statusFg(r.status) }}>
                      {r.status.replace("_", " ")}
                    </span>
                  </div>
                  <h3 className="font-semibold mt-2 line-clamp-2">{r.title}</h3>
                  {r.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{r.description}</p>}
                  <div className="mt-3 pt-3 border-t flex items-center gap-3 text-[11px] font-mono" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city || `${r.lat.toFixed(2)}, ${r.lng.toFixed(2)}`}</span>
                    <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {r.confirms}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {r.comment_count}</span>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function statusBg(s) {
  return { open: "#F3EFEA", verified: "#DBEAFE", in_progress: "#FEF3C7", resolved: "#DCFCE7", closed: "#E5E7EB", duplicate: "#F3F4F6", invalid: "#FEE2E2" }[s] || "#F3EFEA";
}
function statusFg(s) {
  return { open: "#4b4b4b", verified: "#1E40AF", in_progress: "#92400E", resolved: "#166534", closed: "#374151", duplicate: "#6B7280", invalid: "#991B1B" }[s] || "#4b4b4b";
}


/* -------------------- Report detail -------------------- */
function ReportDetail({ id, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get(`/civic/reports/${id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const vote = async (t) => {
    if (!user) return toast.error("Sign in to vote");
    try { await http.post(`/civic/reports/${id}/vote`, { vote_type: t }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const send = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try { await http.post(`/civic/reports/${id}/comments`, { body: comment.trim() }); setComment(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const mark = async (newStatus) => {
    try { await http.post(`/civic/reports/${id}/status`, { status: newStatus, notes: null }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p>Loading…</p>;
  const r = data.report;
  const canManage = user && (user.id === r.reported_by_user_id || user.is_admin);

  return (
    <div data-testid={`civic-detail-${r.id}`}>
      <button onClick={onBack} className="wofy-btn-ghost text-xs mb-4"><ArrowLeft className="w-3 h-3" /> Back</button>

      <div className="grid md:grid-cols-2 gap-8">
        {r.image_url ? (
          <img src={r.image_url} alt="" className="w-full rounded-xl border" style={{ borderColor: "var(--wofy-border)" }} />
        ) : (
          <div className="aspect-video flex items-center justify-center text-6xl rounded-xl" style={{ background: r.category_color + "15" }}>
            {r.category_icon}
          </div>
        )}
        <div>
          <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono uppercase tracking-wider">
            <span className="px-2 py-0.5 rounded-full" style={{ background: r.category_color + "20", color: r.category_color }}>
              {r.category_icon} {r.category_name}
            </span>
            {r.subcategory && <span style={{ color: "var(--wofy-text-soft)" }}>· {r.subcategory}</span>}
            <SeverityDot v={r.severity} /><span style={{ color: "var(--wofy-text-soft)" }}>{r.severity}</span>
            <span className="ml-auto px-2 py-0.5 rounded-full" style={{ background: statusBg(r.status), color: statusFg(r.status) }}>{r.status.replace("_", " ")}</span>
          </div>
          <h1 className="font-display text-3xl mt-3">{r.title}</h1>
          <div className="text-[11px] font-mono mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            #{r.report_number} · reported {new Date(r.created_at).toLocaleString()}
          </div>
          {r.description && <p className="mt-4 text-sm leading-relaxed">{r.description}</p>}
          <div className="mt-4 text-sm flex items-center gap-2 flex-wrap">
            <MapPin className="w-4 h-4 opacity-60" />
            <a href={`https://www.openstreetmap.org/?mlat=${r.lat}&mlon=${r.lng}&zoom=15`} target="_blank" rel="noreferrer" className="underline">
              {r.address || r.city || `${r.lat.toFixed(4)}, ${r.lng.toFixed(4)}`}
            </a>
          </div>
          {r.reported_by_handle && !r.anonymous && (
            <div className="mt-2 text-xs">
              by <Link to={`/u/${r.reported_by_handle}`} className="underline">@{r.reported_by_handle}</Link>
            </div>
          )}

          <div className="mt-6 flex gap-2 flex-wrap">
            <button data-testid="civic-vote-confirm" onClick={() => vote("confirm")} className="wofy-btn-ghost text-xs">
              <ThumbsUp className="w-3 h-3" /> Confirm ({r.confirms})
            </button>
            <button data-testid="civic-vote-still" onClick={() => vote("still_exists")} className="wofy-btn-ghost text-xs">
              <AlertTriangle className="w-3 h-3" /> Still exists ({r.still_exists_votes})
            </button>
            <button data-testid="civic-vote-resolved" onClick={() => vote("resolved")} className="wofy-btn-ghost text-xs">
              <CheckCircle2 className="w-3 h-3" style={{ color: "#16A34A" }} /> Resolved ({r.resolved_votes})
            </button>
            <button data-testid="civic-vote-duplicate" onClick={() => vote("duplicate")} className="wofy-btn-ghost text-xs">
              <XCircle className="w-3 h-3" /> Duplicate ({r.duplicate_votes})
            </button>
          </div>

          {canManage && (
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-2 items-center" style={{ borderColor: "var(--wofy-border)" }}>
              <span className="text-[10px] font-mono uppercase">MARK AS →</span>
              {["verified", "in_progress", "resolved", "closed"].map((s) => (
                <button key={s} onClick={() => mark(s)} data-testid={`civic-mark-${s}`} className="wofy-btn-ghost text-[11px]">
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="mt-10">
        <div className="overline mb-3">STATUS HISTORY</div>
        <ol className="text-xs space-y-1">
          {data.history.map((h) => (
            <li key={h.id} className="flex items-center gap-2">
              <span className="font-mono opacity-60">{new Date(h.changed_at).toLocaleString()}</span>
              <ChevronRight className="w-3 h-3 opacity-40" />
              <b>{h.new_status.replace("_", " ")}</b>
              {h.notes && <span style={{ color: "var(--wofy-text-soft)" }}>· {h.notes}</span>}
            </li>
          ))}
        </ol>
      </div>

      {/* Comments */}
      <div className="mt-10 max-w-2xl">
        <div className="overline mb-3">COMMUNITY ({data.comments.length})</div>
        {data.comments.length === 0 && <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Be the first to add context.</p>}
        <ul className="space-y-4">
          {data.comments.map((c) => (
            <li key={c.id} className="flex gap-3">
              {c.user_avatar ? <img src={c.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover" /> : (
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>
                  {(c.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 text-sm">
                <div className="text-xs">
                  {c.user_handle ? <Link to={`/u/${c.user_handle}`} className="font-semibold hover:underline">{c.user_name || `@${c.user_handle}`}</Link> : <b>{c.user_name || "User"}</b>}
                  <span className="ml-2 font-mono" style={{ color: "var(--wofy-text-soft)" }}>{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap">{c.body}</p>
              </div>
            </li>
          ))}
        </ul>

        {user && (
          <div className="mt-6 flex gap-2">
            <textarea data-testid="civic-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
              placeholder="Add a comment or update…"
              className="flex-1 rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            <button data-testid="civic-comment-send" onClick={send} disabled={busy || !comment.trim()} className="wofy-btn-primary text-sm disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* -------------------- Map -------------------- */
function MapTab({ coords }) {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    http.get(`/civic/reports?limit=200`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !containerRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, { center: [coords.lat, coords.lng], zoom: 12 });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap" }).addTo(mapRef.current);
      }
      if (layerRef.current) layerRef.current.clearLayers();
      else layerRef.current = L.layerGroup().addTo(mapRef.current);
      items.forEach((r) => {
        const marker = L.circleMarker([r.lat, r.lng], {
          radius: 8, weight: 2, color: r.category_color || "#B25D38",
          fillColor: r.category_color || "#B25D38", fillOpacity: 0.7,
        });
        marker.bindPopup(`<b>${r.category_icon} ${r.title}</b><br/><span style="color:#888;font-size:11px">${r.status.replace("_", " ")} · ${r.severity}</span><br/><a href="#" data-id="${r.id}" class="civic-pin-link">Open →</a>`);
        marker.on("popupopen", (e) => {
          const el = e.popup.getElement()?.querySelector(".civic-pin-link");
          if (el) el.addEventListener("click", (ev) => { ev.preventDefault(); setDetail(r.id); });
        });
        marker.addTo(layerRef.current);
      });
    })();
    return () => { cancelled = true; };
  }, [items, coords.lat, coords.lng]);

  if (detail) return <ReportDetail id={detail} onBack={() => setDetail(null)} />;
  return (
    <div data-testid="civic-map-tab">
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
        <div ref={containerRef} className="w-full h-[520px]" />
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>{items.length} reports plotted. Click a pin to open.</p>
    </div>
  );
}


/* -------------------- Submit -------------------- */
function SubmitTab({ categories, coords, onSubmitted }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "", description: "", category_key: "", subcategory: "",
    severity: "normal", lat: coords.lat, lng: coords.lng, address: "",
    city: "", country: "", anonymous: false, is_request: false,
    image_base64: "", mime_type: "image/jpeg",
  });
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm((f) => ({ ...f, lat: coords.lat, lng: coords.lng })); }, [coords.lat, coords.lng]);

  const cat = categories.find((c) => c.key === form.category_key);
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Please pick an image under 6 MB.");
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) return toast.error("JPEG / PNG / WEBP only.");
    change("mime_type", f.type);
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      change("image_base64", String(reader.result).split(",", 2)[1]);
    };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to submit a report");
    if (!form.title || !form.category_key) return toast.error("Title + category required");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.image_base64) delete payload.image_base64;
      await http.post("/civic/reports", payload);
      toast.success("Report submitted");
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!user) return (
    <div className="py-16 text-center rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
      Sign in to submit a report. <Link to="/login" className="underline">Sign in</Link>
    </div>
  );

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-4" data-testid="civic-submit-form">
      <div className="flex gap-2">
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input type="radio" checked={!form.is_request} onChange={() => change("is_request", false)} /> Report a problem
        </label>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input type="radio" checked={form.is_request} onChange={() => change("is_request", true)} /> Request improvement
        </label>
      </div>

      <label className="block">
        <span className="text-xs font-mono uppercase tracking-wider">Title</span>
        <input data-testid="civic-title" required value={form.title} onChange={(e) => change("title", e.target.value)}
          placeholder="e.g. Massive pothole on 5th Ave" className="w-full mt-1 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }} />
      </label>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Category</span>
          <select data-testid="civic-category" required value={form.category_key} onChange={(e) => change("category_key", e.target.value)}
            className="w-full mt-1 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
            <option value="">Choose a category…</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.icon} {c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Subcategory</span>
          <select data-testid="civic-subcategory" value={form.subcategory} onChange={(e) => change("subcategory", e.target.value)} disabled={!cat}
            className="w-full mt-1 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
            <option value="">—</option>
            {(cat?.subcategories || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-xs font-mono uppercase tracking-wider">Severity</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {SEVERITIES.map((s) => (
            <button key={s.v} type="button" data-testid={`civic-severity-${s.v}`} onClick={() => change("severity", s.v)}
              className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${form.severity === s.v ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
              style={form.severity !== s.v ? { borderColor: "var(--wofy-border)" } : undefined}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.dot }} /> {s.label}
            </button>
          ))}
        </div>
      </label>
      {form.severity === "emergency" && (
        <div className="rounded-lg p-3 text-xs" style={{ background: "#FEE2E2", color: "#991B1B" }}>
          <b>Emergency?</b> Call your local emergency services first. Social Pulse is a community record, not a first-responder system.
        </div>
      )}

      <label className="block">
        <span className="text-xs font-mono uppercase tracking-wider">Description</span>
        <div className="mt-1">
          <SmartTextInput
            value={form.description}
            onChange={(v) => change("description", v)}
            placeholder="What did you see? When? Any details helpful for the person coming to fix it…"
            rows={4}
            maxLength={3000}
            testid="civic-description"
          />
        </div>
      </label>

      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Address</span>
          <input data-testid="civic-address" value={form.address} onChange={(e) => change("address", e.target.value)}
            className="w-full mt-1 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">City</span>
          <input data-testid="civic-city" value={form.city} onChange={(e) => change("city", e.target.value)}
            className="w-full mt-1 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Country</span>
          <input data-testid="civic-country" value={form.country} onChange={(e) => change("country", e.target.value)}
            className="w-full mt-1 rounded-lg border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
      </div>

      <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        📍 GPS auto-detected: {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
      </div>

      {/* Photo */}
      {preview ? (
        <div className="relative">
          <img src={preview} alt="" className="w-full max-h-64 object-cover rounded-lg" />
          <button type="button" onClick={() => { setPreview(""); change("image_base64", ""); }} className="wofy-btn-ghost text-xs mt-2">
            Remove photo
          </button>
        </div>
      ) : (
        <label className="block cursor-pointer">
          <div className="rounded-lg border-2 border-dashed py-8 text-center text-sm" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
            <Camera className="w-8 h-8 mx-auto opacity-40 mb-1" />
            Attach a photo (optional but very helpful)
          </div>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="civic-photo-input" />
        </label>
      )}

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input data-testid="civic-anonymous" type="checkbox" checked={form.anonymous} onChange={(e) => change("anonymous", e.target.checked)}
          className="h-4 w-4 rounded border" style={{ borderColor: "var(--wofy-border)", accentColor: "var(--wofy-primary)" }} />
        Submit anonymously (your name won't be shown)
      </label>

      <button data-testid="civic-submit-btn" disabled={busy} className="wofy-btn-primary disabled:opacity-50">
        <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit report"}
      </button>
    </form>
  );
}


/* -------------------- Stats -------------------- */
function StatsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    http.get(`/civic/stats?days=${days}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [days]);
  if (!data) return <p>Loading…</p>;
  return (
    <div data-testid="civic-stats" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          {[7, 30, 90, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`text-xs px-3 py-1.5 rounded-full border ${days === d ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
              style={days !== d ? { borderColor: "var(--wofy-border)" } : undefined}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <StatBox label="Total reports" value={data.total} />
        <StatBox label="Open" value={data.open} />
        <StatBox label="Resolved" value={data.resolved} />
        <StatBox label="Avg resolution" value={data.avg_resolution_hours != null ? `${data.avg_resolution_hours}h` : "—"} />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <section className="wofy-card">
          <div className="overline">BY CATEGORY</div>
          <ul className="mt-4 space-y-2 text-sm">
            {data.by_category.map((c) => (
              <li key={c.key} className="flex items-center gap-2">
                <span className="text-lg">{c.icon}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="font-display text-xl">{c.count}</span>
              </li>
            ))}
            {data.by_category.length === 0 && <li className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No reports yet.</li>}
          </ul>
        </section>
        <section className="wofy-card">
          <div className="overline">BY STATUS</div>
          <ul className="mt-4 space-y-2 text-sm">
            {Object.entries(data.by_status || {}).map(([k, v]) => (
              <li key={k} className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase" style={{ background: statusBg(k), color: statusFg(k) }}>{k.replace("_", " ")}</span>
                <span className="ml-auto font-display text-xl">{v}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}


function StatBox({ label, value }) {
  return (
    <div className="wofy-card !py-4">
      <div className="overline text-[10px]">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}


/* -------------------- Leaders -------------------- */
function LeadersTab() {
  const [data, setData] = useState(null);
  useEffect(() => { http.get("/civic/leaderboard?days=30").then((r) => setData(r.data)).catch(() => setData({ top_reporters: [] })); }, []);
  if (!data) return <p>Loading…</p>;
  return (
    <section className="wofy-card" data-testid="civic-leaders">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <div className="overline">TOP CIVIC REPORTERS · LAST 30 DAYS</div>
      </div>
      {data.top_reporters.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No reports yet. Be the first.</p>
      ) : (
        <ol className="space-y-3">
          {data.top_reporters.map((p, i) => (
            <li key={p.user_id} className="flex items-center gap-3">
              <span className="w-8 text-xs font-mono opacity-60">#{i + 1}</span>
              {p.avatar ? <img src={p.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-display" style={{ background: "var(--wofy-surface)" }}>{(p.name || "?").slice(0, 1).toUpperCase()}</div>
              )}
              <div className="flex-1 min-w-0">
                {p.handle ? <Link to={`/u/${p.handle}`} className="font-semibold hover:underline">{p.name || `@${p.handle}`}</Link> : <b>{p.name || "Anonymous"}</b>}
                <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                  {p.reports} report{p.reports !== 1 ? "s" : ""} · {p.resolved} resolved
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
